import { cn } from "~/lib/utils";

interface BalanceProgressBarProps {
  percentage: number;
  leftToSpend: string;
  available: string;
  showAsterisk?: boolean;
  showAvailableToSpend?: boolean;
  onClick?: () => void;

  bgColor?: string;
  largeFonts?: boolean;
  /** `solid`: a filled grey track, no outline (FSA Spender card). */
  variant?: "outline" | "solid";
}

const BalanceProgressBar: React.FC<BalanceProgressBarProps> = ({
  percentage,
  leftToSpend,
  available,
  showAsterisk = false,
  onClick,
  showAvailableToSpend = true,
  bgColor = "bg-primary",
  largeFonts = false,
  variant = "outline",
}) => {
  const clampedPct = Math.min(100, Math.max(0, percentage));
  const percentageDisplay = `${clampedPct}%`;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={cn(
        "my-1 flex flex-1 items-center gap-3 lg:max-w-[1450px]",
        // No handler (account card, COM-17) — don't advertise a click target.
        onClick && "hover:cursor-pointer",
      )}
      onClick={onClick}
    >
      <div
        className={cn(
          "relative h-3.5 flex-1 overflow-hidden rounded-[1.5625rem]",
          variant === "solid" ? "bg-gray-04" : "border-2 border-gray-02",
        )}
        role="progressbar"
        aria-valuenow={clampedPct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={cn(
            "absolute h-3.5 rounded-[1.5625rem] transition-[width] duration-300 ease-out",
            variant === "solid" ? "left-0 top-0" : "-left-0.5 -top-0.5",
            bgColor,
          )}
          style={{
            width: clampedPct > 0 ? `max(${percentageDisplay}, 8px)` : "0%",
          }}
        />
      </div>
      {showAvailableToSpend && (
        <div className="whitespace-nowrap text-right leading-tight">
          <div
            className={cn(
              "font-grotesque font-medium text-teal-01",
              largeFonts ? "" : "text-xs",
            )}
          >
            <span className="font-medium">{leftToSpend}</span>
            <span className="font-light">
              /{available}
              {showAsterisk ? "*" : ""}
            </span>
          </div>
          <div className="text-left text-xs text-teal-01">
            Available to Spend
          </div>
        </div>
      )}
    </div>
  );
};

export default BalanceProgressBar;
